import { useEffect, useRef, useState } from "react";

import { HeatmapLegend, YEAR_ROW_HEIGHT, YearGrid } from "@/surfaces/stats/chart/Heatmap";
import { useChartHover } from "@/surfaces/stats/chart/HoverCard";
import { Skeleton } from "@/surfaces/stats/Skeleton";
import { statsHabit, statsHistory, statsRecall, type StatsHabit } from "@/lib/stats";
import { useCollectionChanged } from "@/lib/useCollectionChanged";
import { usePhone } from "@/lib/usePhone";
import { useRefreshOnFocus } from "@/lib/useRefreshOnFocus";
import { EXTEND_NEW_BY, type ReviewDeckOption, type ReviewLanding } from "@/lib/review";

import { DayBars, DayBarsSkeleton } from "./DayBars";
import { EnterKey } from "./EnterKey";
import { cardsNote, reviewsNote } from "./sessionRules";

/**
 * `7a`. The screen a session starts from.
 *
 * **It answers two questions and no others**: *what am I studying*, and *have I been showing up*.
 * Forecasts, per-deck retention and difficulty mix all live in Stats — the frame's own note says
 * so, and the reason is that this is a launch pad rather than a dashboard.
 */

interface Props {
  landing: ReviewLanding;
  /**
   * These counts belong to a sitting that has just ended and the fresh ones are in flight (日本-404).
   *
   * **Only the figures wait; the deck list does not.** Which decks exist, what they are called, how
   * many cards they hold and where the buttons are is structure and is not stale — only the numbers
   * moved. So this draws placeholders in four places and changes nothing else, which is Stats'
   * skeleton rule applied to the one panel on this screen that was still exempt from it: the heatmap
   * below has always done exactly this on the same transition, because its state lives here and
   * unmounts with the sitting, while the deck counts live in `Review` and survived it.
   */
  pending: boolean;
  deckForHistory: string | null;
  onStudy: (deck: string) => void;
  /** まだまだ — take on five more of this subdeck's new cards today (日本-451). */
  onExtend: (deck: string) => void;
  /** The subdeck a まだまだ press is in flight for, or null. Disables every button rather than only
   *  the pressed one: the write lifts ancestors, so a second press elsewhere would race the first
   *  and land on figures that are about to move. */
  extending: string | null;
  /**
   * The surface's one-line failure notice, or null.
   *
   * **It has to be drawn HERE, because the landing is the only screen まだまだ can be pressed
   * from.** `Review` holds `notice` for both halves of the surface and `SessionChrome` draws it
   * inside a sitting — so a failed extend set a string nothing was rendering, and the press was
   * silent: the counts simply did not move, which is exactly what a deck with nothing left to take
   * on looks like. That indistinguishability is the reason the notice exists at all.
   */
  notice: string | null;
  onOpenStats: () => void;
}

export function Landing({
  landing,
  pending,
  deckForHistory,
  onStudy,
  onExtend,
  extending,
  notice,
  onOpenStats,
}: Props) {
  const { history, retention30 } = useHistory(deckForHistory);
  const isPhone = usePhone();

  /**
   * **The current year on top, earlier years counting backwards** (Alex, 9 Aug 2026).
   *
   * The server answers oldest-first, so taking `years[0]` gave the big card 2019 and listed
   * 2020…2026 below it — the year you are actually in was the last row of a ledger. Reversed here
   * rather than on the server, because Stats' own History tab wants the ascending order it already
   * has and this is a statement about *this* screen.
   */
  // The same hover card Stats draws, and `YearGrid` builds its content from `dayHover` internally —
  // so a cell cannot describe itself one way here and another way there. It was simply never wired:
  // the grid takes the two handlers and I passed neither, so the landing's heatmap was the only
  // chart in the app with no hover (Alex, 9 Aug 2026).
  const { onEnter, onLeave, card: hoverCard } = useChartHover();

  const years = history?.heatmap.years ?? [];
  const currentYear = years[years.length - 1];
  const earlierYears = years.slice(0, -1).reverse();

  /**
   * The shortcuts the deck cards advertise — the numbered chip on each, and `↵` on the first.
   *
   * The frame prints them and says they are "learned from the Setup screen's key list, not printed
   * as a hint above the decks", which is an argument for not explaining them here — not for them
   * not existing. They did not: the chips were drawn and nothing listened.
   *
   * A deck with nothing due is skipped rather than started, matching its own disabled button.
   */
  const decks = landing.decks;
  // **Still computed from the last known counts while pending**, deliberately. Deck *identity* is
  // not stale — only the figures are — and the alternative is `↵` doing nothing for a round trip on
  // the one screen whose whole job is to start a sitting.
  const enterTarget = decks.find((option) => option.due + option.new > 0);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) {
        return;
      }
      // **`↵` starts the first deck that HAS something**, not the first deck (Alex, 9 Aug 2026).
      // The leftmost card is often finished for the day — its own button is disabled — so `↵`
      // aimed at it did nothing at all, which reads as the key being broken rather than as the
      // deck being done. A number key still means *that* deck and nothing else: it is a choice,
      // and silently starting a different one would be worse than doing nothing.
      const deck = event.key === "Enter" ? enterTarget : decks[Number(event.key) - 1];
      if (!deck || deck.due + deck.new === 0) return;
      event.preventDefault();
      onStudy(deck.name);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [decks, enterTarget, onStudy]);

  return (
    <div
      className={`mx-auto flex w-full max-w-[var(--content-max)] flex-col ${
        isPhone
          ? "gap-[12px] px-[16px] pb-[16px] pt-[14px]"
          : "gap-[26px] px-[18px] pb-[22px] pt-[22px] md:px-10 md:pb-10 md:pt-8"
      }`}
    >
      {/* The eyebrow goes on a phone, where the bar directly above it already says 復習. Two
          headings for one screen is 30px of a 844px viewport spent saying it twice. */}
      {!isPhone && (
        <span className="text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
          学習
        </span>
      )}

      {/* **The failure notice, drawn where the press that produces it lives.** `--accent-hi` and
          `role="status"` match `SessionChrome`'s, which is the same string slot for the same kind
          of event — a write that did not land. It sits above the deck row rather than beside the
          button because the row it belongs to may be the third one down a card, and a notice the
          reader has to hunt for is the silence it replaces. It is not a slot: with nothing to say
          it renders nothing at all, so the deck cards do not move. */}
      {notice && (
        <span
          role="status"
          data-check="review.landing.notice"
          className="text-meta text-accent-hi"
        >
          {notice}
        </span>
      )}

      {/* Parent decks are the unit of choice because that is the unit of intent. Number keys pick
          one and ↵ starts it — learned from Setup's key list, not printed as a hint here. */}
      <div className="flex flex-col gap-5 lg:flex-row">
        {landing.decks.map((deck, index) => (
          <DeckCard
            key={deck.name}
            deck={deck}
            pending={pending}
            index={index}
            // **The primary card is the one `↵` will start**, not the leftmost (Alex, 9 Aug 2026).
            // `primary` drives the heavy border, the dark button AND the `↵` hint, so with the
            // first deck finished for the day all three sat on a disabled card while `↵` correctly
            // started the next one. Falls back to the first card when nothing is due anywhere, so
            // the row never loses its anchor.
            primary={deck.name === (enterTarget?.name ?? landing.decks[0]?.name)}
            onStudy={onStudy}
            onExtend={onExtend}
            extending={extending}
          />
        ))}
      </div>

      {/* **A skeleton, not a pop-in.** The decks arrive from one request and the heatmap from a
          much heavier one, so the panel used to appear from nowhere a second later and shove the
          page around. Structure never waits: the card, its heading and its legend are drawn at
          once and only the grid is a placeholder. Stats' own rule, and its own component. */}
      <div className="rounded-[var(--r-4)] border border-line-strong bg-panel px-5 py-[18px] shadow-[var(--sh-frame)]">
        <div className="flex items-center gap-3 pb-3">
          <span className="text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
            {currentYear?.year ?? ""}
          </span>
          <div className="flex-1" />
          {history && (
            <HeatmapLegend
              thresholds={history.heatmap.thresholds}
              inProgress={history.heatmap.inProgress}
            />
          )}
        </div>
        {/* `justify-center` so a part-finished year — January, three columns wide — sits in the
            middle of the card rather than clinging to the left edge of a 1,000px panel. The
            `p/-m` pair is the hover ring's reach: `overflow-x` computes `overflow-y` to `auto` as
            well, so a ring drawn outside the padding box is clipped rather than overhanging. */}
        <div className="-m-[4px] flex justify-center overflow-x-auto p-[4px]">
          {currentYear ? (
            <YearGrid year={currentYear} onEnter={onEnter} onLeave={onLeave} />
          ) : (
            <Skeleton height={YEAR_ROW_HEIGHT} width={520} />
          )}
        </div>
        <div className="mt-3 flex items-center gap-5 border-t border-line-soft pt-3">
          {history ? (
            <>
              <Figure value={history.streak.current.toLocaleString()} label="日連続" />
              <span className="h-[18px] w-px bg-line-soft" />
              <Figure
                value={retention30 === null ? "—" : `${(retention30 * 100).toFixed(1)}%`}
                label="定着率・30日"
              />
            </>
          ) : (
            <Skeleton height={22} width={220} />
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onOpenStats}
            className="cursor-pointer text-secondary text-[color:var(--accent-link)] hover:underline"
          >
            統計 ›
          </button>
        </div>
      </div>

      {earlierYears.length > 0 && (
        <div className="rounded-[var(--r-4)] border border-line-strong bg-panel px-5 py-4 shadow-[var(--sh-frame)]">
          <span className="text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
            過去の年
          </span>
          {/* **Three columns, and only the middle one scrolls** — the shape `stats/History.tsx`
              arrived at and for its reason. Built as a row per year, the year number and the
              figures sit *inside* the scroller and are clipped off the panel's right edge at the
              width this actually renders at; and with the figures sharing a flex row with an
              auto-width grid, a long average overlapped `perfect year`. `YEAR_ROW_HEIGHT` is what
              keeps the three columns aligned. */}
          <div className="mt-3 flex items-start gap-[10px]">
            <div className="flex shrink-0 flex-col gap-[9px]">
              {earlierYears.map((year) => (
                <span
                  key={year.year}
                  className="flex w-[34px] justify-end pt-[1px] font-mono text-meta text-ink-3"
                  style={{ height: YEAR_ROW_HEIGHT }}
                >
                  {year.year}
                </span>
              ))}
            </div>
            <div className="-m-[4px] min-w-0 flex-1 overflow-x-auto p-[4px]">
              {/* **`mx-auto w-max`, not `justify-center`** (Alex, 9 Aug 2026 — the ledger sat left
                  with a wide gap beside it). Centring a scroll container's contents with
                  `justify-center` clips the LEFT edge as soon as they overflow, which they do at
                  390px; an auto margin on a `w-max` child centres when there is room and simply
                  stops when there is not.

                  `items-start` inside it, or a part-finished year's ~30 auto-width columns are
                  stretched across the width of the widest full year and render as evenly spaced
                  stripes. */}
              <div className="mx-auto flex w-max flex-col items-start gap-[9px]">
                {earlierYears.map((year) => (
                  <YearGrid key={year.year} year={year} onEnter={onEnter} onLeave={onLeave} />
                ))}
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-[9px]">
              {earlierYears.map((year) => (
                <div
                  key={year.year}
                  className="flex w-[132px] flex-col items-end gap-[2px] pt-[1px]"
                  style={{ height: YEAR_ROW_HEIGHT }}
                >
                  {/* Two decimals (Alex, 9 Aug 2026). Unrounded it ran to
                      `149.45081967213116/day` and pushed `perfect year` out of its own column.
                      Stats prints the same figure to nought — the value agrees, the precision
                      does not. */}
                  <span className="font-mono text-meta text-ink-2">
                    {year.average.toFixed(2)}回/日
                  </span>
                  <span className="text-meta text-ink-4">
                    {year.perfect ? "皆勤" : `未実施${year.missed}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Portalled and `position: fixed`, so it does not matter that both grids above live inside
          `overflow-x-auto` boxes — rendered inside one, it would be clipped by the very container
          the pointer is in. */}
      {hoverCard}
    </div>
  );
}

function DeckCard({
  deck,
  pending,
  index,
  primary,
  onStudy,
  onExtend,
  extending,
}: {
  deck: ReviewDeckOption;
  pending: boolean;
  index: number;
  primary: boolean;
  onStudy: (deck: string) => void;
  onExtend: (deck: string) => void;
  extending: string | null;
}) {
  const total = deck.due + deck.new;
  return (
    /**
     * **The focused card is grey with a red left edge, and its button is red — a DELIBERATE
     * deviation from the canvas** (Alex, 15 Aug 2026), which draws `2px solid #e4e2dc` around the
     * card and `#e4e2dc` behind the button. Written down rather than silently applied, on the
     * same terms as Review's `Card ⋯`: a difference from the handoff that is not recorded reads as
     * drift the next time somebody reconciles the two.
     *
     * It also settles a tension the canvas left in place. THEME.md's rule is that **red marks what
     * wants you**, and a deck with cards due is the single largest instance of that in the app —
     * the figure inside the card was already `--accent` for exactly that reason, while the border
     * and the button announcing the same deck were bone. Bone is what the finished screen wears.
     *
     * The left edge rather than the whole border: a 2px red rectangle around a card whose own
     * headline figure is red makes the panel read as failing (`--accent-ground` territory), where
     * a rule down one side is the emphasis a nobori already uses on the rail.
     */
    <div
      // The hook exists for one assertion and it is worth the attribute: `pending` claims the card
      // does not reflow when the real figures arrive, and nothing checked it — the first cut of
      // `DayBarsSkeleton` was 8px short a row and every check still passed. `check-review.mjs`
      // measures this box either side of the swap.
      data-check="review.deck.card"
      className={[
        "flex flex-1 flex-col gap-4 rounded-[var(--r-4)] border border-line-strong bg-panel px-[22px] pb-[18px] pt-5 shadow-[var(--sh-frame)]",
        primary ? "border-l-[3px] border-l-accent" : "",
      ].join(" ")}
    >
      <div className="flex items-start gap-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[22px]/[1.2] font-semibold text-ink">{deck.name}</span>
          <span className="text-secondary text-ink-3">
            {deck.subdecks.length > 0 && `サブデッキ${deck.subdecks.length} · `}
            {deck.inRotation.toLocaleString()}枚を運用中
          </span>
        </div>
        <span className="inline-flex h-5 items-center rounded-[var(--r-2)] px-1.5 font-mono text-meta font-semibold text-ink-3 [background:var(--sunken)]">
          {index + 1}
        </span>
      </div>

      <div className="flex items-end gap-6">
        <Count value={deck.due} label="要復習" tone="action" check="review.deck.due" pending={pending} />
        <Count value={deck.new} label="新規" tone="new" check="review.deck.new" pending={pending} />
        <div className="flex-1" />
        {/* Measured or absent. A figure with no measurement behind it is worse than no figure.
            **No skeleton while pending, for the same reason**: this line is legitimately missing on
            a deck with too little review history, so a placeholder here would promise a number that
            may never arrive — the one thing a skeleton must not do. */}
        {!pending && deck.estimatedMinutes !== null && (
          <span className="text-secondary text-ink-3">約{deck.estimatedMinutes}分</span>
        )}
      </div>

      {/* **The same two bars the sitting wears** (日本-389, Alex's call — the handoff recommends it
          and does not draw it). The whole point of a day-scoped bar is that it is true when you are
          *not* reviewing, so a pair that only appeared mid-session would give up half its value:
          this is where you decide whether there is an evening's work left before opening anything.
          Per deck card rather than one summed pair, because the bars are deck-scoped and a single
          pair on a screen listing several decks is a number with no name on it.
          `compact` drops the labels — the card names the deck two rows up, and position carries
          which bar is which exactly as it does on the phone. */}
      {pending ? (
        <DayBarsSkeleton />
      ) : (
        <DayBars
          day={deck.day}
          cardNote={cardsNote(deck.day)}
          reviewNote={reviewsNote(deck.day)}
          compact
        />
      )}

      {deck.subdecks.length > 0 && (
        <div className="flex flex-col border-t border-line-soft">
          {deck.subdecks.map((row, rowIndex) => (
            <div
              key={row.fullName}
              className={[
                "flex items-center gap-2.5 py-2",
                rowIndex < deck.subdecks.length - 1 ? "border-b border-line-soft" : "",
              ].join(" ")}
            >
              <span className="flex-1 text-secondary text-ink-2">{row.name}</span>
              {/* **A deck with nothing due carries no red anywhere** — THEME.md's own consequence
                  of "red marks what wants you", stated for exactly this row. A zero in the accent
                  is a backlog that is not there.
                  These two are the half of the card a client-side optimistic figure could never
                  produce — `position.counts` describes the deck the sitting selected, not each of
                  its children — which is why the whole card waits on one answer rather than showing
                  a fresh headline over rows that still sum to the old one. */}
              {pending ? (
                <>
                  <span className="flex w-11 justify-end">
                    <Skeleton height={11} width={22} />
                  </span>
                  <span className="flex w-9 justify-end">
                    <Skeleton height={11} width={18} />
                  </span>
                </>
              ) : (
                <>
                  <span
                    className="w-11 text-right font-mono text-secondary"
                    style={{ color: row.due ? "var(--accent)" : "var(--ink-off)" }}
                  >
                    {row.due}
                  </span>
                  <span
                    className="w-9 text-right font-mono text-secondary"
                    style={{ color: row.new ? "var(--ink)" : "var(--ink-off)" }}
                  >
                    {row.new}
                  </span>
                </>
              )}
              {/* **まだまだ — take on five more of this deck's new cards today** (日本-451).
                  "Not yet, not even close": the deck has said it is done for the day and you
                  disagree, which is exactly when this is pressed. It is the only loud copy in the
                  app and that is deliberate rather than drift.

                  **Per subdeck row, because a row names the deck that HOLDS the cards.** On the
                  card's own header it would have had to guess which subdeck was meant; here the
                  server lifts this deck and its ancestors and nothing has to be inferred.

                  **Absent, not disabled, when there is nothing to take on** — `pending` aside, a
                  deck with no unseen new cards offers no press that could do anything, and a greyed
                  control invites one. Same reading the app already gives a deck with nothing due,
                  which carries no red and no affordance. The slot is held either way so the row's
                  right edge does not move down the card.

                  **Not `--accent`.** 新規 is bone because new cards are an opportunity rather than
                  an obligation (THEME.md), and the control that raises it inherits that. */}
              {/* **The slot holds the button's HEIGHT as well as its width.** Width alone keeps the
                  right edge still; without the height a row whose button is absent is 6px shorter
                  than its neighbours — measured on the real landing, and it reads as the rows
                  having been laid out carelessly rather than as one deck having nothing to take
                  on. */}
              <span className="flex h-[26px] w-[84px] shrink-0 items-center justify-end">
                {row.canExtend && (
                  <button
                    type="button"
                    disabled={extending !== null}
                    onClick={() => onExtend(row.fullName)}
                    data-check="review.deck.extend"
                    data-deck={row.fullName}
                    title={`${row.name}の新規をあと${EXTEND_NEW_BY}枚`}
                    className="inline-flex h-[26px] items-center rounded-[var(--r-2)] border border-line-strong bg-raised px-3 font-semibold text-meta leading-none text-ink-2 hover:border-ink-3 hover:bg-line-soft hover:text-ink disabled:border-line-soft disabled:bg-panel disabled:text-ink-off"
                  >
                    まだまだ
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1" />
      <button
        type="button"
        // **Enabled while pending, even on a deck that looked finished.** The disabled state is
        // computed from counts that have just stopped being true, and getting it wrong in that
        // direction is the worse failure: a deck you have not touched would refuse to open. Starting
        // one that really is done answers with `7e`, which says so.
        disabled={!pending && total === 0}
        onClick={() => onStudy(deck.name)}
        data-check="review.start"
        /* The deck name is user content and stays English inside the Japanese verb — but the verb
           WRAPS it, so nothing can recover the name by stripping a known prefix. It is data. */
        data-deck={deck.name}
        className={[
          "flex h-14 items-center justify-center gap-2.5 rounded-[var(--r-3)] disabled:opacity-40",
          // `text-white` rather than `--ink`, matching the two fills the app already has on this
          // red (`TabBar`'s active tab, `SignIn`). Hover goes UP — `tokens.css` calls that the
          // single most likely thing to get wrong by mapping the old palette across, and this
          // button had no hover at all to get wrong until now.
          primary
            ? "bg-accent text-white hover:bg-accent-hover"
            : "border border-line-strong bg-panel text-ink",
        ].join(" ")}
      >
        <span className="text-[19px]/[1] font-semibold">{deck.name}を復習</span>
        {/* **The key is DRAWN, not typed, and both halves of that are load-bearing** (日本-500).
            It was `↵` (U+21B5) in `--accent-tint-2`, which was wrong three ways at once and only
            the colour was noticed: that token is a red-tinted panel GROUND (`tokens.css`), so it
            resolved to `#37120d` and read as a black hole punched in the button — the only place
            in the app that token is used as a foreground rather than a background.

            The other two are font metrics and are why a colour fix alone was not enough. The glyph
            carried **6px of ink against the label's 20px**, and sat **1.3px low** — 22% of its own
            height — with neither adjustable from CSS. Drawing it sets weight and position here,
            and costs nothing else: `currentColor` inherits the button's `text-white`, so there is
            no second colour to keep in step and the disabled `opacity-40` carries the arrow too.

            **`-0.75px` is CALIBRATED, not derived. Do not replace it with a formula.** Two were
            tried and both failed against Alex's eye: matching the arrow's ink centroid to the
            label's read LOW, and matching its ink bounding box read HIGH. The value came from a
            five-step ladder judged by eye, and measures ~+0.6px on the arrow's ink box against the
            label's — i.e. beside CJK text this mark wants to sit slightly BELOW geometric centre
            to look level.

            **And there is no single right value, because the label is USER CONTENT.** It is
            `{deck.name}を復習`, so the ink box it is judged against moves with the deck name:
            measured at +0.56px on 日本語, +0.85 on 漢字, +0.16 on `Vocab`, and −0.24 on `Japanese`,
            where the `p` descender drags the label's ink down. A spread of ~1.1px, which is why
            this stays a judgement rather than a computed value. Calibrated on 日本語; re-tuning it
            against a seeded `Japanese` deck would move it for the wrong reason.

            **`check-review.mjs` is NOT a claim about that spread**, and cannot be: it measures the
            arrow's ink against the label's BOX, which `line-height: 1` pins at 19px whatever the
            deck is called — a deterministic −0.22px, asserted with slack. What it guards is that
            the mark is drawn at all and that this translate is still doing something; the spread
            above is guarded by Alex's eye and by nothing else. */}
        {primary && <EnterKey check="review.start.key" offsetPx={-0.75} />}
      </button>
    </div>
  );
}

function Count({
  value,
  label,
  tone,
  check,
  pending,
}: {
  value: number;
  label: string;
  tone: "action" | "new";
  /** `checks/` hook — the browser checks read the figure, and its label is about to be Japanese. */
  check: string;
  /** Draw the block instead of the number. **The label is not skeletoned** — `StatSkeleton`'s rule:
   *  it is structure, it is already known, and it is what says which number is coming. Two anonymous
   *  grey blocks would not. */
  pending: boolean;
}) {
  return (
    // **The skeleton has to be this element's FIRST CHILD**, because that is what
    // `check-review.mjs` reads out of `[data-check="review.deck.*"]` to prove the pair is drawn.
    <div className="flex flex-col gap-0.5" data-check={check} data-pending={pending || undefined}>
      {pending ? (
        // 40px tall and roughly two digits wide, so the row's baseline does not jump when the real
        // figure lands. Not 100% wide: a block the width of the column reads as a bar rather than
        // as a number about to appear.
        <Skeleton height={40} width={54} />
      ) : (
        <span
          className="text-[40px]/[1] font-semibold tracking-[-.02em]"
          style={{
            // 要復習 is red and 新規 is bone — the theme's rule that red marks what wants you, and
            // new cards are an opportunity rather than an obligation. A zero is neither, so it takes
            // `--ink-off` whichever figure it is.
            color:
              value === 0 ? "var(--ink-off)" : tone === "action" ? "var(--accent)" : "var(--ink)",
          }}
        >
          {value}
        </span>
      )}
      <span className="text-eyebrow uppercase tracking-[.08em] text-ink-3">{label}</span>
    </div>
  );
}

function Figure({ value, label }: { value: string; label: string }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-[24px]/[1] font-semibold text-ink">{value}</span>
      <span className="text-secondary text-ink-3">{label}</span>
    </span>
  );
}

/**
 * The heatmap comes from Stats' own route, and the deck it describes is the **habit** scope's, not
 * this screen's picker: `stats/scope.py` answers "did you sit down and study" across the chosen
 * deck's whole family, which is the question a streak asks. Passing a subdeck here would make the
 * streak disagree with the one Stats prints for the same collection.
 *
 * It is fetched separately from the deck list so the decks — the reason the screen exists — are on
 * screen immediately rather than behind a revlog scan.
 *
 * **`/habit`, not `/history`** (日本-718). The grid and the streak are all this screen draws, and
 * `/history` also runs `graphs()` and two FSRS simulations: ~5.7 s on production for every read
 * after a sitting — which is every time this screen is reached — against one revlog scan here.
 */
function useHistory(deck: string | null): {
  history: StatsHabit | null;
  retention30: number | null;
} {
  const [history, setHistory] = useState<StatsHabit | null>(null);
  const [retention30, setRetention30] = useState<number | null>(null);

  /**
   * **Re-read on focus and on a collection change, exactly like the deck counts** (日本-388).
   *
   * These two were a `[deck]`-only effect, so the streak and the heatmap were as stale as the deck
   * cards were: left on this screen overnight, the grid was missing yesterday and the streak was a
   * day short until you navigated away and back. Focus is the half that matters — a day rollover
   * writes nothing, so there is no collection event to hear.
   *
   * **No `deck` guard here, unlike `Review`'s landing read**, and the difference is not an
   * oversight: `Landing` is only rendered when there is no sitting, so being mounted *is* the
   * guard. `deck` is still passed as the hook's key, because a different deck is a different
   * question.
   */
  const generation = useRef(0);
  const read = () => {
    if (!deck) return;
    const issued = ++generation.current;
    const current = () => issued === generation.current;
    statsHabit(deck)
      .then((result) => {
        if (!current()) return;
        setHistory(result);
        // **Then the full `/history`, unread, and that is two jobs rather than none.** It is what
        // records today's forecast for 予測精度's back-test — `_history` writes one snapshot per
        // day, and until 日本-718 this screen was the read that reliably happened every day — and
        // it leaves Stats' History warm for the visit that so often follows a sitting. Issued after
        // `/habit` answers, never beside it: both run on the batch handle's one worker, and racing
        // them could put this screen's grid behind the simulations again. Only for a read that is
        // still current: a superseded one has a successor that will issue its own.
        statsHistory(deck).catch(() => {});
      })
      .catch(() => {
        // A landing screen that cannot draw a heatmap is still a landing screen. The decks and
        // their counts are the load-bearing half and they came from a different request.
      });
    // The 30-day figure is the retention TABLE's, not a second calculation — `stats/recall.py`
    // owns the young/mature split and the exclusions (`type = 1 and ease > 0`) that make it agree
    // with the Recall tab. Deliberately a separate request, and the last one issued: it is the
    // most expensive read on the screen and the least of what the screen is for.
    statsRecall(deck)
      .then((result) => {
        if (!current()) return;
        setRetention30(result.retention.find((row) => row.period === "month")?.all.rate ?? null);
      })
      .catch(() => {});
  };

  useRefreshOnFocus(read, deck);
  useCollectionChanged(read);

  return { history, retention30 };
}
